import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { assetUrlsQueryOptions } from '@/entities/asset';
import { fontUrlIds, FontGrid, toFontSpecimen, type BrandFontItem } from '@/entities/brand-font';

/**
 * The library's fonts as rows, each opening with an "Aa" in its own typeface.
 *
 * The list-view counterpart of `FontCards`, and separate from the host for the same reason: the
 * font files are fetched only where something is drawn with them.
 */
export const FontRows = ({
  fonts,
  selectedIds,
  openFontId,
  onChoose,
  onToggleSelect,
}: {
  /**
   * Memoise this in the host. A fresh array each render rebuilds the specimens below, and every
   * rebuild re-registers every `FontFace` with the document.
   */
  fonts: readonly BrandFontItem[];
  /** Bare font ids, not the library's qualified keys: the grid below knows only about fonts. */
  selectedIds: ReadonlySet<string>;
  openFontId?: string;
  onChoose: (fontId: string) => void;
  onToggleSelect: (fontId: string) => void;
}) => {
  const urls = useQuery(assetUrlsQueryOptions(fontUrlIds(fonts)));
  const specimens = useMemo(
    () => fonts.map((font) => toFontSpecimen(font, urls.data)),
    [fonts, urls.data],
  );

  return (
    <FontGrid
      fonts={fonts}
      specimens={specimens}
      selectedIds={selectedIds}
      openFontId={openFontId}
      onChoose={onChoose}
      onToggleSelect={onToggleSelect}
    />
  );
};
